import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  CalendarDays,
  CalendarRange,
  Camera,
  Inbox,
  Clock,
  LayoutGrid,
  Link2,
  LogOut,
  Megaphone,
  Menu,
  MessageSquareText,
  Sparkles,
  X,
  CreditCard,
} from "lucide-react";
import { api, clearTokens, getRefreshToken } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { STEP_ROUTES, isNextStep, useOnboarding, type StepKey } from "@/lib/onboarding";
import { useApprovalCount } from "@/lib/approvals";
import { Logo } from "@/components/Logo";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { PublisherAlert } from "./PublisherAlert";
import { VerifyEmailBanner } from "./VerifyEmailBanner";

// =============================================================================
// The application frame.
//
// A dark rail carrying the wordmark, a light working area. The rail is dark
// for a practical reason as well as a visual one: the logo is a JPEG on solid
// black and composites cleanly only on a dark surface.
//
// Navigation is grouped and ordered by the job to be done — set up the brand,
// then make content, then manage what goes out — and the group a first-time
// user needs is the one that is pre-expanded and carries the "next" marker.
// =============================================================================

type NavItem = {
  to: string;
  labelKey: string;
  icon: ReactNode;
  /** Ties the item to an onboarding step so it can show done / next / locked. */
  step?: StepKey;
  /** A live count, e.g. posts waiting on a decision. */
  badge?: string;
};

type NavGroup = { titleKey: string; items: NavItem[] };

const GROUPS: NavGroup[] = [
  {
    titleKey: "nav.groups.setup",
    items: [
      { to: "/brands", labelKey: "nav.brands", icon: <LayoutGrid size={17} />, step: "brand" },
      { to: "/connect", labelKey: "nav.connect", icon: <Link2 size={17} />, step: "connect" },
      { to: "/voice", labelKey: "nav.voice", icon: <MessageSquareText size={17} />, step: "voice" },
    ],
  },
  {
    titleKey: "nav.groups.create",
    items: [
      { to: "/photo", labelKey: "nav.photo", icon: <Camera size={17} />, step: "post" },
      { to: "/campaigns", labelKey: "nav.campaigns", icon: <Megaphone size={17} /> },
      { to: "/plan-month", labelKey: "nav.planMonth", icon: <CalendarRange size={17} /> },
    ],
  },
  {
    titleKey: "nav.groups.manage",
    items: [
      { to: "/approvals", labelKey: "nav.approvals", icon: <Inbox size={17} /> },
      { to: "/calendar", labelKey: "nav.calendar", icon: <CalendarDays size={17} /> },
      { to: "/schedule", labelKey: "nav.schedule", icon: <Clock size={17} /> },
      { to: "/billing", labelKey: "nav.billing", icon: <CreditCard size={17} /> },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const reset = useAuthStore((s) => s.reset);
  const onboarding = useOnboarding();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Navigating should close the drawer; without this a tap on a link leaves
  // the overlay covering the page it just opened.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  async function signOut() {
    const refreshToken = getRefreshToken();
    // Best-effort revoke; the local session is cleared either way so a
    // network failure can't leave the user stuck signed in.
    if (refreshToken) {
      try {
        await api.post("/auth/logout", { refreshToken });
      } catch {
        /* ignore */
      }
    }
    clearTokens();
    reset();
    // window.location, explicitly: `location` is shadowed by useLocation()
    // above, and a full page load is wanted here anyway so no stale query
    // cache or store state survives the sign-out.
    window.location.assign("/login");
  }

  const rail = (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto bg-night-900 px-3 py-4">
      <Link to="/" className="mb-5 flex items-center px-2 py-1">
        <Logo className="h-[22px]" alt={t("appName")} white />
      </Link>

      {!onboarding.complete && !onboarding.loading ? <SetupProgress /> : null}

      {GROUPS.map((group) => (
        <div key={group.titleKey} className="mb-4">
          <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/35">
            {t(group.titleKey)}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavItemLink item={item} />
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="mt-auto border-t border-white/10 pt-3">
        {/* The name and address were the one piece of the rail that said
            something and did nothing. They are the natural way in to "who am
            I, what am I on, what have I paid" — so they are the link. */}
        <NavLink
          to="/account"
          className={({ isActive }) =>
            `block rounded-lg px-3 py-2 transition-colors ${
              isActive ? "bg-white/10" : "hover:bg-white/5"
            }`
          }
        >
          <p className="truncate text-sm font-medium text-white/90">{user?.name}</p>
          <p className="truncate text-xs text-white/40">{user?.email}</p>
        </NavLink>
        <button onClick={() => void signOut()} className="nav-link w-full">
          <LogOut size={17} />
          {t("nav.signOut")}
        </button>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-surface">
      {/* Fixed rail on desktop */}
      <aside className="hidden w-64 shrink-0 lg:block">
        <div className="fixed inset-y-0 w-64">{rail}</div>
      </aside>

      {/* Drawer on mobile */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-night-950/60"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <div className="absolute inset-y-0 start-0 w-72 shadow-lift">{rail}</div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-border bg-white/85 backdrop-blur">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileOpen((o) => !o)}
                className="btn-ghost -ms-2 p-2 lg:hidden"
                aria-label={t("nav.menu")}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
              <WorkspaceSwitcher />
            </div>
            <NextStepPill />
          </div>
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 animate-fade-up px-4 py-6 sm:px-6 lg:py-8">
          {/* Above the page, not inside one screen: a post can be scheduled from
              several places and the owner has no reason to visit the calendar
              to find out that nothing is publishing. It renders nothing at all
              while the publisher is healthy. */}
          <PublisherAlert />
          <VerifyEmailBanner />
          {children}
        </main>
      </div>
    </div>
  );
}

/** A nav row, annotated with where the owner is in setup. */
function NavItemLink({ item: base }: { item: NavItem }) {
  const { t } = useTranslation();
  const { steps, next } = useOnboarding();
  const waiting = useApprovalCount();

  const item: NavItem = {
    ...base,
    badge:
      base.to === "/approvals" && (waiting.data?.waiting ?? 0) > 0
        ? String(waiting.data!.waiting)
        : undefined,
  };

  const state = item.step ? steps.find((s) => s.key === item.step) : undefined;

  /**
   * `item.step` must exist before this can be the next step.
   *
   * Without that guard, `next?.key === item.step` compared undefined to
   * undefined the moment setup finished — `next` goes null, and every row
   * that has no step (Campaigns, Calendar, Schedule) matched and lit up.
   * The guidance is for someone finding their way; once they have, it goes.
   */
  const isNext = isNextStep(item.step, next);
  const isLocked = state?.locked ?? false;

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        [
          "nav-link",
          isActive ? "nav-link-active" : "",
          isNext ? "nav-link-next" : "",
          // Dimmed, not disabled: someone who knows where they are going is
          // never stopped by our idea of the right order.
          isLocked && !isActive ? "opacity-40" : "",
        ].join(" ")
      }
    >
      {item.icon}
      <span className={`flex-1 truncate ${isNext ? "font-semibold" : ""}`}>{t(item.labelKey)}</span>
      {/* How many posts are waiting on a decision. Sits ahead of the step
          markers because an unapproved post is the thing actually holding
          publishing up. */}
      {item.badge ? (
        <span className="rounded-full bg-primary-400/25 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-primary-100">
          {item.badge}
        </span>
      ) : isNext ? (
        <span className="rounded-full bg-primary-400/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-200">
          {t("onboarding.next")}
        </span>
      ) : null}
    </NavLink>
  );
}

/** "2 of 4 done" with a bar, shown only while setup is unfinished. */
function SetupProgress() {
  const { t } = useTranslation();
  const { doneCount, total } = useOnboarding();
  const pct = Math.round((doneCount / total) * 100);

  return (
    <div className="mb-5 rounded-xl bg-white/5 px-3 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-white/50">
        {t("onboarding.setup")}
      </p>
      <p className="mt-0.5 text-sm text-white/90">
        {t("onboarding.progress", { done: doneCount, total })}
      </p>
      <div
        className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"
        role="progressbar"
        aria-valuenow={doneCount}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div
          className="h-full rounded-full bg-primary-300 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * The single next action, in the top bar.
 *
 * Deliberately one destination and not a checklist panel: the sidebar already
 * carries the full sequence, and a second list of the same four things is
 * noise. This is a shortcut, not a second source of truth.
 */
function NextStepPill() {
  const { t } = useTranslation();
  const { next, complete, loading } = useOnboarding();

  if (loading || complete || !next) return null;

  return (
    <Link
      to={STEP_ROUTES[next.key]}
      className="group inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-[13px] font-medium text-primary-800 transition-colors hover:bg-primary-100"
    >
      <Sparkles size={14} className="text-primary-600" />
      <span className="hidden sm:inline">{t(`onboarding.steps.${next.key}.cta`)}</span>
      <span className="sm:hidden">{t("onboarding.next")}</span>
      <ArrowRight
        size={14}
        className="flip-rtl transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  );
}
